import type { FC } from 'react';
import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';

import type { LcpnTheme } from '../../styles/theme';
import { Icon } from '../Icon';
import { useFavouritesData } from '../../providers/FavouritesProvider';
import { useNavigationBarData } from '../NavigationBar/NavigationBarProvider';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  wrapper: {
    position: 'fixed',
    bottom: theme.spacing(4),
    zIndex: 1000,
    right: theme.spacing(4),
    height: theme.spacing(12),
    width: theme.spacing(12),
  },
  [`@media screen and (max-width: ${theme.breakPoints.desktop}px)`]: {
    wrapper: {
      display: 'none',
    },
  },
  button: {
    position: 'absolute',
    bottom: 0,
    margin: 'auto',
    padding: 0,
    left: 0,
    height: theme.spacing(12),
    width: theme.spacing(12),
    cursor: 'pointer',
    background: theme.colors['blue-100'],
    border: 'none',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 ${theme.spacing(0.5)} ${theme.spacing(2)} rgba(0,0,0,0.2)`,
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: `0 ${theme.spacing(1)} ${theme.spacing(3)} rgba(0,0,0,0.3)`,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  badge: {
    position: 'absolute',
    top: '-0.125rem',
    right: '-0.125rem',
    background: '#e63946',
    color: 'white',
    borderRadius: '50%',
    width: theme.spacing(5),
    height: theme.spacing(5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.fontSizes.p3,
    fontWeight: 'bold',
  },
}));

interface Props {
  onClick: () => void;
}

export const FavouritesButton: FC<Props> = ({ onClick }) => {
  const classes = useStyles();
  const { poiFavourites, heatmapFavourites } = useFavouritesData();
  const { selectedExperience } = useNavigationBarData();

  // Calculate count based on current section
  const favouritesCount = useMemo(() => {
    if (selectedExperience === 'pinMap') {
      return poiFavourites.length;
    }
    if (selectedExperience === 'heatMap') {
      return heatmapFavourites.length;
    }
    // Fallback to total if experience is not set
    return poiFavourites.length + heatmapFavourites.length;
  }, [selectedExperience, poiFavourites.length, heatmapFavourites.length]);

  const hasFavourites = favouritesCount > 0;

  return (
    <div className={classes.wrapper}>
      <button
        className={classes.button}
        onClick={onClick}
        data-tour="favourites-button"
        aria-label="View favourites"
        disabled={false}
      >
        <Icon
          variant={hasFavourites ? 'HeartFilled' : 'Heart'}
          width={20}
          height={20}
          style={{ color: 'white' }}
        />
        {hasFavourites && (
          <div className={classes.badge}>{favouritesCount}</div>
        )}
      </button>
    </div>
  );
};
